import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';
import regression from 'regression';
import Select from 'react-select';
import { Card } from 'antd';

const HullChart = ({ sampleData }) => {
    console.log("data data", sampleData)
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [selectedBreakEvents, setSelectedBreakEvents] = useState([]);
    const [overallSeries, setOverallSeries] = useState([]);
    const [segmentedSeries, setSegmentedSeries] = useState([]);
    const [movingAvgSeries, setMovingAvgSeries] = useState([]);
    const [overallOptions, setOverallOptions] = useState({});
    const [segmentedOptions, setSegmentedOptions] = useState({});
    const [movingAvgOptions, setMovingAvgOptions] = useState({});

    // Convert string/null values to numbers
    const parseValue = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
    };

    // Clean and process data
    const processData = (data) => {
        return data.map(item => {
            const processedItem = { ...item };

            // Convert all numeric fields from strings to numbers
            Object.keys(processedItem).forEach(key => {
                if (key !== 'date' && key !== 'time' && key !== 'eventType' && key !== 'hull_event' && key !== 'imo_number') {
                    processedItem[key] = parseValue(processedItem[key]);
                }
            });

            return processedItem;
        });
    };

    const processedData = processData(sampleData?.series);

    // Dynamically get available metrics (only numeric fields with at least some non-null values)
    const getAvailableMetrics = () => {
        if (!processedData.length) return [];

        const firstItem = processedData[0];
        const potentialMetrics = Object.keys(firstItem).filter(key =>
            key !== 'date' && key !== 'time' && key !== 'eventType' && key !== 'hull_event' && key !== 'imo_number'
        );

        // Filter metrics that have at least some non-null values
        return potentialMetrics.filter(metric => {
            const hasNonNullValues = processedData.some(item => item[metric] !== null);
            return hasNonNullValues;
        });
    };

    const getHullEventTypes = () => {
        return [...new Set(processedData.map(item => item.hull_event).filter(event => event))];
    };

    const availableMetrics = getAvailableMetrics();
    const hullEventTypes = getHullEventTypes();

    // Convert hull event types to react-select format
    const hullEventOptions = hullEventTypes.map(hullEvent => ({
        value: hullEvent,
        label: hullEvent
    }));

    // Initialize with first available metric and hull event type
    useEffect(() => {
        if (availableMetrics.length > 0 && selectedMetrics.length === 0) {
            setSelectedMetrics([availableMetrics[0]]);
        }
    }, [availableMetrics, hullEventTypes]);

    // Dynamic color generation
    const generateColors = (items) => {
        const baseColors = [
            '#dc3545', '#007bff', '#28a745', '#ffc107', '#6f42c1', '#fd7e14',
            '#17a2b8', '#e83e8c', '#6c757d', '#343a40', '#f8f9fa', '#e9ecef',
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3',
            '#54A0FF', '#5F27CD', '#00D2D3', '#FF9FF3', '#54A0FF', '#FECA57'
        ];

        const colors = {};
        items.forEach((item, index) => {
            colors[item] = baseColors[index % baseColors.length];
        });
        return colors;
    };

    const metricColors = generateColors(availableMetrics);
    const hullEventColors = generateColors(hullEventTypes);
    const segmentColors = generateColors(Array.from({ length: 20 }, (_, i) => `segment${i}`));

    // Handle metric selection
    const handleMetricChange = (metric) => {
        setSelectedMetrics(prev =>
            prev.includes(metric)
                ? prev.filter(m => m !== metric)
                : [...prev, metric]
        );
    };

    // Handle react-select change for break events
    const handleBreakEventChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setSelectedBreakEvents(values);
    };


    // Create boundary annotations for ApexCharts
    const createBoundaryAnnotations = () => {

        return sampleData?.category?.length ? sampleData?.category.map((boundary, index) => ({
            y: boundary.min,
            y2: boundary.max,
            fillColor: boundary.color,
            opacity: 0.3,
            borderColor: 'transparent',
            label: {
                text: boundary.label,
                position: 'right',
                style: {
                    color: '#666',
                    background: 'rgba(255,255,255,0.8)',
                    fontSize: '10px'
                }
            }
        })) : null;
    };

    // Linear regression calculation using regression library
    const calculateLinearRegressionLib = (data) => {
        if (data.length < 2) return null;

        try {
            const points = data.map(point => [point.x, point.y]);
            const result = regression.linear(points);

            return {
                slope: result.equation[0],
                intercept: result.equation[1],
                rSquared: result.r2
            };
        } catch (error) {
            console.error('Regression calculation error:', error);
            return null;
        }
    };

    // Generate regression line points
    const generateRegressionLine = (data, slope, intercept) => {
        const minX = Math.min(...data.map(p => p.x));
        const maxX = Math.max(...data.map(p => p.x));
        return [
            { x: minX, y: slope * minX + intercept },
            { x: maxX, y: slope * maxX + intercept }
        ];
    };

    // Find break events and create segments for multiple break events
    const createSegmentsMultiple = (data, breakHullEvents) => {
        const segments = [];
        let currentSegment = [];

        data.forEach((item, index) => {
            if (breakHullEvents.includes(item.hull_event)) {
                // End current segment if it has data
                if (currentSegment.length > 0) {
                    segments.push([...currentSegment]);
                }
                // Start new segment with the break event
                currentSegment = [{ ...item, x: new Date(item.date).getTime() }];
            } else {
                // Add to current segment
                currentSegment.push({ ...item, x: new Date(item.date).getTime() });
            }
        });

        // Add final segment if it has data
        if (currentSegment.length > 0) {
            segments.push(currentSegment);
        }

        return segments;
    };

    // Create break line ribbons for ALL hull events (without duplicate labels)
    const createAllBreakLineRibbons = (data) => {
        const breakLines = [];

        data.forEach((item, index) => {
            if (item.hull_event && item.hull_event !== 'Noon at Sea') {
                breakLines.push({
                    x: new Date(item.date).getTime(),
                    strokeDashArray: 5,
                    borderColor: hullEventColors[item.hull_event],
                    borderWidth: 2,
                    opacity: 0.8,
                    label: {
                        text: `${item.hull_event.toUpperCase()}`,
                        position: 'top',
                        style: {
                            color: '#fff',
                            background: hullEventColors[item.hull_event],
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }
                    }
                });
            }
        });

        return breakLines;
    };

    // Create break line ribbons for selected hull event types only (without duplicate labels)
    const createSelectedBreakLineRibbons = (data, breakHullEvents) => {
        const breakLines = [];

        data.forEach((item, index) => {
            if (breakHullEvents.includes(item.hull_event)) {
                breakLines.push({
                    x: new Date(item.date).getTime(),
                    strokeDashArray: 5,
                    borderColor: hullEventColors[item.hull_event],
                    borderWidth: 2,
                    opacity: 0.8,
                    label: {
                        text: `${item.hull_event.toUpperCase()}`,
                        position: 'top',
                        style: {
                            color: '#fff',
                            background: hullEventColors[item.hull_event],
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }
                    }
                });
            }
        });

        return breakLines;
    };

    // Calculate moving average
    const calculateMovingAverage = (data, metric, windowSize = 3) => {
        const result = [];
        for (let i = windowSize - 1; i < data.length; i++) {
            const window = data.slice(i - windowSize + 1, i + 1);
            const validValues = window.filter(item => item[metric] !== null);

            if (validValues.length > 0) {
                const average = validValues.reduce((sum, item) => sum + item[metric], 0) / validValues.length;
                result.push({
                    x: new Date(data[i].date).getTime(),
                    y: average,
                    date: data[i].date
                });
            }
        }
        return result;
    };

    // Calculate 3-month moving average with breakpoints
    const calculate3MonthMovingAverage = (data, metric) => {
        // Sort data by date
        const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
        const result = [];

        for (let i = 0; i < sortedData.length; i++) {
            const currentDate = new Date(sortedData[i].date);
            const threeMonthsAgo = new Date(currentDate);
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

            // Get data points within the last 3 months
            const windowData = sortedData.filter(item => {
                const itemDate = new Date(item.date);
                return itemDate >= threeMonthsAgo && itemDate <= currentDate && item[metric] !== null;
            });

            if (windowData.length > 0) {
                const average = windowData.reduce((sum, item) => sum + item[metric], 0) / windowData.length;
                result.push({
                    x: currentDate.getTime(),
                    y: average,
                    date: sortedData[i].date
                });
            }
        }

        return result;
    };

    useEffect(() => {
        if (selectedMetrics.length === 0) return;

        // Filter data to only include points with non-null values for at least one selected metric
        const validData = processedData.filter(item =>
            selectedMetrics.some(metric => item[metric] !== null)
        );

        if (validData.length === 0) return;

        // Create series for multiple metrics
        const createMultiMetricSeries = (data, type = 'overall') => {
            const series = [];

            // Add data series for each selected metric
            selectedMetrics.forEach(metric => {
                const metricData = data
                    .filter(item => item[metric] !== null)
                    .map((item, index) => ({
                        x: new Date(item.date).getTime(),
                        y: item[metric],
                        hull_event: item.hull_event,
                    }));

                if (metricData.length > 0) {
                    series.push({
                        name: `${metric.replace(/_/g, ' ')} Data`,
                        data: metricData,
                        type: 'scatter',
                        color: metricColors[metric]
                    });

                    // Add regression line for each metric
                    const regression = calculateLinearRegressionLib(metricData);
                    if (regression) {
                        const regressionLine = generateRegressionLine(metricData, regression.slope, regression.intercept);
                        series.push({
                            name: `${metric.replace(/_/g, ' ')} Regression (R²=${regression.rSquared.toFixed(3)})`,
                            data: regressionLine,
                            type: 'line',
                            color: metricColors[metric],
                            strokeDashArray: 5
                        });
                    }
                }
            });

            return series;
        };

        // Create segmented series with proper regression breaking for multiple break events
        const createSegmentedSeries = (data, breakHullEvents) => {
            const series = [];

            // Add data points for each metric
            selectedMetrics.forEach(metric => {
                const metricData = data
                    .filter(item => item[metric] !== null)
                    .map((item, index) => ({
                        x: new Date(item.date).getTime(), // FIXED: Use timestamp consistently
                        y: item[metric],
                        hull_event: item.hull_event,
                        date: item.date
                    }));

                if (metricData.length > 0) {
                    series.push({
                        name: `${metric.replace(/_/g, ' ')} Data`,
                        data: metricData,
                        type: 'scatter',
                        color: metricColors[metric]
                    });

                    // Create segments and regression lines for each metric
                    const segments = createSegmentsMultiple(data.filter(item => item[metric] !== null), breakHullEvents);
                    segments.forEach((segment, segmentIndex) => {
                        const segmentData = segment.map(item => ({
                            x: new Date(item.date).getTime(),
                            y: item[metric]
                        }));

                        if (segmentData.length > 1) {
                            const regression = calculateLinearRegressionLib(segmentData);
                            if (regression) {
                                const regressionLine = generateRegressionLine(segmentData, regression.slope, regression.intercept);
                                series.push({
                                    name: `${metric.replace(/_/g, ' ')} Segment ${segmentIndex + 1} (R²=${regression.rSquared.toFixed(3)})`,
                                    data: regressionLine,
                                    type: 'line',
                                    color: segmentColors[`segment${segmentIndex}`],
                                    strokeWidth: 3
                                });
                            }
                        }
                    });
                }
            });

            return series;
        };

        const create3MonthMovingAvgSeries = (data) => {
            const series = [];

            selectedMetrics.forEach(metric => {
                const movingAvgData = calculate3MonthMovingAverage(data, metric);

                if (movingAvgData.length > 0) {
                    series.push({
                        name: `${metric.replace(/_/g, ' ')} 3-Month Moving Average`,
                        data: movingAvgData,
                        type: 'line',
                        color: metricColors[metric],
                        strokeWidth: 2
                    });
                }
            });

            return series;
        };

        // Generate all series
        const overallScatterSeries = createMultiMetricSeries(validData, 'overall');
        const segmentedScatterSeries = createSegmentedSeries(validData, selectedBreakEvents);
        const movingAvgScatterSeries = create3MonthMovingAvgSeries(validData);

        setOverallSeries(overallScatterSeries);
        setSegmentedSeries(segmentedScatterSeries);
        setMovingAvgSeries(movingAvgScatterSeries);

        // Create break lines (removed duplicate annotations)
        const allBreakLineRibbons = createAllBreakLineRibbons(validData);
        const selectedBreakLineRibbons = createSelectedBreakLineRibbons(validData, selectedBreakEvents);

        // Create boundary annotations
        const boundaryAnnotations = createBoundaryAnnotations();

        // Set chart options
        const baseOptions = {
            chart: {
                type: 'line',
                height: 400,
                zoom: { enabled: true },
                toolbar: { show: true },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800
                },
            },
            xaxis: {
                type: 'datetime',
                title: {
                    text: 'Date',
                    style: {
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151'
                    }
                },
                labels: {
                    format: 'MMM dd',
                    style: {
                        colors: '#6b7280'
                    },
                    rotate: -45,
                    datetimeUTC: false
                },
                axisBorder: {
                    color: '#e5e7eb'
                },
                axisTicks: {
                    color: '#e5e7eb'
                }
            },
            yaxis: {
                title: { text: 'Values' },
                labels: {
                    formatter: (val) => val?.toFixed(2) || '0'
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'center'
            },
            tooltip: {
                shared: false,
                intersect: false,
                x: {
                    format: 'dd MMM yyyy'
                },
                y: {
                    formatter: (val) => val?.toFixed(2) || '0'
                }
            },
            grid: {
                borderColor: '#f3f4f6',
                strokeDashArray: 3,
                xaxis: {
                    lines: {
                        show: true
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                }
            },
            markers: {
                size: 4,
                hover: { size: 6 }
            }
        };

        setOverallOptions({
            ...baseOptions,
            title: {
                text: 'Overall Regression Analysis with All Hull Events',
                align: 'center',
                style: { fontSize: '16px', fontWeight: 'bold' }
            },
            annotations: {
                xaxis: allBreakLineRibbons,
                yaxis: boundaryAnnotations
            }
        });

        setSegmentedOptions({
            ...baseOptions,
            title: {
                text: `Segmented Regression Analysis with Selected Hull Events`,
                align: 'center',
                style: { fontSize: '16px', fontWeight: 'bold' }
            },
            annotations: {
                xaxis: selectedBreakLineRibbons,
                yaxis: boundaryAnnotations
            }
        });

        setMovingAvgOptions({
            ...baseOptions,
            title: {
                text: '3-Month Moving Average Analysis',
                align: 'center',
                style: { fontSize: '16px', fontWeight: 'bold' }
            },
            annotations: {
                yaxis: boundaryAnnotations
            }
        });

    }, [selectedMetrics, selectedBreakEvents, sampleData]);

    const selectedBreakEventOptions = selectedBreakEvents.map(hullEvent => ({
        value: hullEvent,
        label: hullEvent
    }));

    return (

        <div >
<Card className="mb-2" style={{ width: '100%' }}>
                <label className="form-label fw-semibold">Select Metrics:</label>
                <div className="d-flex flex-wrap gap-2">
                    {availableMetrics.map(metric => (
                        <div key={metric} className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                value={metric}
                                id={metric}
                                checked={selectedMetrics.includes(metric)}
                                onChange={() => handleMetricChange(metric)}
                            />
                            <label className="form-check-label" htmlFor={metric}>
                                <span
                                    className="badge me-1"
                                    style={{ backgroundColor: metricColors[metric] }}
                                >
                                    ●
                                </span>
                                {metric.replace(/_/g, ' ')}
                            </label>
                        </div>
                    ))}
                </div>

            </Card>
            {selectedMetrics?.length > 0 && (
                <>
<Card className="mb-2" style={{ width: '100%' }}>
                        <Chart
                            options={{
                                ...overallOptions,
                                xaxis: {
                                    type: 'datetime',
                                    title: {
                                        text: 'Date',
                                        style: {
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151'
                                        }
                                    },
                                    labels: {
                                        format: 'MMM dd',
                                        style: {
                                            colors: '#6b7280'
                                        },
                                        rotate: -45
                                    },
                                    axisBorder: {
                                        color: '#e5e7eb'
                                    },
                                    axisTicks: {
                                        color: '#e5e7eb'
                                    }
                                },
                            }}
                            series={overallSeries}
                            type="line"
                            height={400}
                        />

                    </Card>


<Card className="mb-2" style={{ width: '100%' }}>
                        <label className="form-label fw-semibold">Select Hull Event Types for Break Analysis:</label>
                        <Select
                            isMulti
                            options={hullEventOptions}
                            value={selectedBreakEventOptions}
                            onChange={handleBreakEventChange}
                            placeholder="Select hull events..."
                            className="mb-3"
                        />

                        <Chart
                            options={{
                                ...segmentedOptions,
                                xaxis: {
                                    type: 'datetime',
                                    title: {
                                        text: 'Date',
                                        style: {
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151'
                                        }
                                    },
                                    labels: {
                                        format: 'MMM dd',
                                        style: {
                                            colors: '#6b7280'
                                        },
                                        rotate: -45
                                    },
                                    axisBorder: {
                                        color: '#e5e7eb'
                                    },
                                    axisTicks: {
                                        color: '#e5e7eb'
                                    }
                                },
                            }}
                            series={segmentedSeries}
                            type="line"
                            height={400}
                        />
                    </Card>

<Card className="mb-2" style={{ width: '100%' }}>
                        <Chart
                            options={{
                                ...movingAvgOptions,
                                xaxis: {
                                    type: 'datetime',
                                    title: {
                                        text: 'Date',
                                        style: {
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151'
                                        }
                                    },
                                    labels: {
                                        format: 'MMM dd',
                                        style: {
                                            colors: '#6b7280'
                                        },
                                        rotate: -45
                                    },
                                    axisBorder: {
                                        color: '#e5e7eb'
                                    },
                                    axisTicks: {
                                        color: '#e5e7eb'
                                    }
                                },
                            }}
                            series={movingAvgSeries}
                            type="line"
                            height={400}
                        />
                    </Card>
                </>
            )}

            {selectedMetrics.length === 0 && (
                <div className="alert alert-info">
                    Please select at least one metric to display the charts.
                </div>
            )}

        </div>
    );
};

export default HullChart;